import type { RevenueColumn } from './revenueApi';

/**
 * Формула колонки в виде списка звеньев. На сервер она уходит прежним текстом «имя(номер) + имя(номер) - ...»:
 * номер колонки - источник истины, имя только для читаемости, поэтому переименование колонок формулу не ломает.
 */
export type Operator = '+' | '-' | '*' | '/';
export type Paren = '(' | ')';

export type FormulaToken =
  | { kind: 'ref'; id: number }
  | { kind: 'num'; text: string }
  | { kind: 'op'; value: Operator }
  | { kind: 'paren'; value: Paren };

export type ColumnIndex = Record<number, RevenueColumn>;

export const OPERATOR_LABELS: Record<Operator, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };

const REFERENCE = /(\w+)\((\d+)\)/g;
const NUMBER_PATTERN = /^\d+(\.\d+)?$/;
const MAX_NUMBER_LENGTH = 15;
// Колонка pharmacyNum - номер аптеки, а не сумма: в расчетах от нее смысла нет
const NOT_FOR_FORMULAS = 'pharmacyNum';

export function indexColumns(columns: RevenueColumn[]): ColumnIndex {
  const index: ColumnIndex = {};
  columns.forEach((item) => {
    index[item.id] = item;
  });
  return index;
}

// ---------------------------------------------------------------------------
// Разбор и сборка текста формулы
// ---------------------------------------------------------------------------

/**
 * Текст формулы -> звенья. Возвращает null, если формулу нельзя показать в конструкторе:
 * служебная (в фигурных скобках), с неизвестной колонкой или с незнакомыми символами.
 */
export function parseFormula(text: string, index: ColumnIndex): FormulaToken[] | null {
  const source = text.trim();
  if (source === '') return [];
  if (source.charAt(0) === '{') return null;

  const tokens: FormulaToken[] = [];
  let rest = source;
  while (rest.trim() !== '') {
    const match = /^\s*(?:(\d+(?:\.\d+)?)|[A-Za-z_]\w*\((\d+)\)|([-+*/()]))/.exec(rest);
    if (!match) return null;
    if (match[1] !== undefined) {
      tokens.push({ kind: 'num', text: match[1] });
    } else if (match[2] !== undefined) {
      const id = Number(match[2]);
      if (!index[id]) return null;
      tokens.push({ kind: 'ref', id });
    } else {
      const symbol = match[3];
      if (symbol === '(' || symbol === ')') tokens.push({ kind: 'paren', value: symbol });
      else tokens.push({ kind: 'op', value: symbol as Operator });
    }
    rest = rest.slice(match[0].length);
  }
  return tokens;
}

/** Звенья -> текст формулы для сервера */
export function serializeFormula(tokens: FormulaToken[], index: ColumnIndex): string {
  return tokens
    .map((token) => {
      if (token.kind === 'ref') {
        const column = index[token.id];
        // Колонка пропала из справочника: сервер отклонит формулу с понятным сообщением
        return `${column ? column.cName.trim() : 'unknown'}(${token.id})`;
      }
      if (token.kind === 'num') return token.text;
      return token.value;
    })
    .join(' ')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')');
}

/** Формула словами: «Наличные − Инкассация», с названиями колонок из отчетов */
export function describeFormula(tokens: FormulaToken[], index: ColumnIndex): string {
  return tokens
    .map((token) => {
      if (token.kind === 'ref') return index[token.id] ? index[token.id].displayName.trim() : `поле ${token.id}`;
      if (token.kind === 'num') return token.text.replace('.', ',');
      if (token.kind === 'op') return OPERATOR_LABELS[token.value];
      return token.value;
    })
    .join(' ')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')');
}

/** Номера колонок, которые использует формула (без повторов, в порядке появления) */
export function usedIds(tokens: FormulaToken[]): number[] {
  const seen: Record<number, true> = {};
  const ids: number[] = [];
  tokens.forEach((token) => {
    if (token.kind === 'ref' && !seen[token.id]) {
      seen[token.id] = true;
      ids.push(token.id);
    }
  });
  return ids;
}

// ---------------------------------------------------------------------------
// Какие звенья можно добавить
// ---------------------------------------------------------------------------

interface Position {
  /** Следующим должно идти число, поле или открывающая скобка */
  expectOperand: boolean;
  depth: number;
}

function positionOf(tokens: FormulaToken[]): Position {
  let expectOperand = true;
  let depth = 0;
  tokens.forEach((token) => {
    if (token.kind === 'ref' || token.kind === 'num') expectOperand = false;
    else if (token.kind === 'op') expectOperand = true;
    else if (token.value === '(') {
      depth += 1;
      expectOperand = true;
    } else {
      depth = Math.max(0, depth - 1);
      expectOperand = false;
    }
  });
  return { expectOperand, depth };
}

export type Addable = 'operand' | 'open' | 'close' | Operator;

/** Можно ли сейчас добавить звено: недопустимые последовательности («+ +», «) (») конструктор не пропускает */
export function canAdd(tokens: FormulaToken[], what: Addable): boolean {
  const { expectOperand, depth } = positionOf(tokens);
  if (what === 'operand' || what === 'open') return expectOperand;
  if (what === 'close') return !expectOperand && depth > 0;
  if (what === '-') {
    // Минус допустим и как знак числа: в начале формулы и после открывающей скобки
    const last = tokens[tokens.length - 1];
    return !expectOperand || !last || (last.kind === 'paren' && last.value === '(');
  }
  return !expectOperand;
}

/** Число, введенное пользователем: целое или с десятичной частью (запятая и точка равнозначны) */
export function normalizeNumber(input: string): string | null {
  const text = input.trim().replace(',', '.');
  if (!NUMBER_PATTERN.test(text) || text.length > MAX_NUMBER_LENGTH) return null;
  // Ведущие нули не нужны: 007 -> 7
  return text.replace(/^0+(?=\d)/, '');
}

// ---------------------------------------------------------------------------
// Проверка
// ---------------------------------------------------------------------------

export interface FormulaCheck {
  /** Формула пуста */
  empty: boolean;
  /** Что не так; пустая строка, если формула готова к сохранению */
  problem: string;
}

/** Проверка формулы по звеньям: баланс скобок, чередование полей и знаков, законченность */
export function checkTokens(tokens: FormulaToken[]): FormulaCheck {
  if (tokens.length === 0) return { empty: true, problem: '' };

  let expectOperand = true;
  let depth = 0;
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token.kind === 'ref' || token.kind === 'num') {
      if (!expectOperand) return { empty: false, problem: 'Между полями и числами нужен знак действия' };
      expectOperand = false;
    } else if (token.kind === 'op') {
      const unary = token.value === '-' || token.value === '+';
      if (expectOperand && !unary) return { empty: false, problem: `Перед знаком «${OPERATOR_LABELS[token.value]}» нужно поле или число` };
      expectOperand = true;
    } else if (token.value === '(') {
      if (!expectOperand) return { empty: false, problem: 'Перед скобкой нужен знак действия' };
      depth += 1;
    } else {
      if (expectOperand) return { empty: false, problem: 'Перед закрывающей скобкой нужно поле или число' };
      if (depth === 0) return { empty: false, problem: 'Лишняя закрывающая скобка' };
      depth -= 1;
    }
  }
  if (expectOperand) return { empty: false, problem: 'Формула не закончена: после знака нужно поле или число' };
  if (depth > 0) return { empty: false, problem: 'Не закрыта скобка' };
  return { empty: false, problem: '' };
}

/** Делитель не число и не константа: если в нем окажется 0, результат формулы станет 0 */
export function hasVariableDivisor(tokens: FormulaToken[]): boolean {
  for (let i = 0; i < tokens.length - 1; i += 1) {
    const token = tokens[i];
    if (token.kind !== 'op' || token.value !== '/') continue;
    const next = tokens[i + 1];
    if (next.kind === 'ref') return true;
    if (next.kind === 'paren' && next.value === '(') return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Какие колонки можно подставлять в формулу
// ---------------------------------------------------------------------------

/** Колонки, формулы которых (прямо или через другие колонки) используют колонку с номером targetId */
export function dependentsOf(columns: RevenueColumn[], targetId: number): number[] {
  const found: Record<number, true> = {};
  let changed = true;
  while (changed) {
    changed = false;
    columns.forEach((item) => {
      if (found[item.id] || item.id === targetId) return;
      const formula = (item.formula || '').trim();
      if (!formula || formula.charAt(0) === '{') return;
      const re = new RegExp(REFERENCE.source, 'g');
      let match = re.exec(formula);
      while (match) {
        const id = Number(match[2]);
        if (id === targetId || found[id]) {
          found[item.id] = true;
          changed = true;
          break;
        }
        match = re.exec(formula);
      }
    });
  }
  return Object.keys(found).map(Number);
}

/**
 * Колонки, которые предлагаются в конструкторе: только числовые, без самой редактируемой колонки
 * и без тех, что сами на нее опираются (иначе формулы замкнутся в круг).
 */
export function pickerColumns(columns: RevenueColumn[], editingId: number | null): RevenueColumn[] {
  const blocked: Record<number, true> = {};
  if (editingId !== null) {
    blocked[editingId] = true;
    dependentsOf(columns, editingId).forEach((id) => {
      blocked[id] = true;
    });
  }
  return columns.filter((item) => {
    if (blocked[item.id]) return false;
    if (item.cName.trim() === NOT_FOR_FORMULAS) return false;
    const type = item.cType.trim().toUpperCase();
    return type === 'INT' || type === 'FLOAT';
  });
}

// ---------------------------------------------------------------------------
// Пробный расчет
// ---------------------------------------------------------------------------

export interface TrialResult {
  value: number;
  /** Делитель оказался нулем: как и в отчетах, результат равен 0 */
  zeroDivision: boolean;
}

/** Считает формулу на введенных значениях тем же порядком действий, что и сервер; null, если формула неполная */
export function evaluateTokens(tokens: FormulaToken[], values: Record<number, number>, integer: boolean): TrialResult | null {
  if (checkTokens(tokens).problem || tokens.length === 0) return null;

  let pos = 0;
  const peekOp = (...ops: string[]): string | null => {
    const token = tokens[pos];
    return token && token.kind === 'op' && ops.indexOf(token.value) >= 0 ? token.value : null;
  };

  const factor = (): number => {
    const token = tokens[pos];
    pos += 1;
    if (token.kind === 'op') {
      const inner = factor();
      return token.value === '-' ? -inner : inner;
    }
    if (token.kind === 'paren') {
      const inner = expression();
      pos += 1;
      return inner;
    }
    if (token.kind === 'num') return Number(token.text);
    const value = values[token.id];
    return typeof value === 'number' && isFinite(value) ? value : 0;
  };

  const term = (): number => {
    let left = factor();
    let op = peekOp('*', '/');
    while (op) {
      pos += 1;
      const right = factor();
      left = op === '*' ? left * right : left / right;
      op = peekOp('*', '/');
    }
    return left;
  };

  const expression = (): number => {
    let left = term();
    let op = peekOp('+', '-');
    while (op) {
      pos += 1;
      const right = term();
      left = op === '+' ? left + right : left - right;
      op = peekOp('+', '-');
    }
    return left;
  };

  const raw = expression();
  if (!isFinite(raw)) return { value: 0, zeroDivision: true };
  return { value: integer ? Math.round(raw) : raw, zeroDivision: false };
}
